import { useCallback, useEffect, useRef, useState } from "react"
import { APIS } from "../../../config/apis"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { ServerRequest } from "../../../utils/apiweb"
import { onRequestError, onRequestNoSuccess } from "../../../utils/requests"
import { InputNumber, Modal } from "../../common"
import { useForm, useManagedContext } from "../../hooks"
import { precisionRound } from "../../../utils/convert"
import { useReporte } from "../../hooks/useReporte"
import { printTicket, printTickets } from "../../../utils/printTicket"
import { getVencimientoVigente } from "../CobranzasModal"


const cobranzaInit = {
    importeCobro: 0,
    importePago: 0,
    observacion: ""
}

const CobranzaCajaModal = ({ recibos, mediosPagos, cajaResumen, onCancel, onConfirm }) => {
    const { setIsLoading } = useManagedContext()
    const observacionRef = useRef(null)
    const [isProcessing, setIsProcessing] = useState(false)

    const [formValues, formHandle, , formSet] = useForm(cobranzaInit)

    useEffect(() => {
        let importeCobro = 0;
        for (let r = 0; r < recibos.length; r++) {
            importeCobro += getVencimientoVigente(recibos[r]).importe;
        }
        let importePago = 0;
        for (let mp = 0; mp < mediosPagos.length; mp++) {
            importePago += mediosPagos[mp].importeCobro;
        }
        formSet({...cobranzaInit, importeCobro: precisionRound(importeCobro), importePago: precisionRound(importePago)})
    }, [recibos, mediosPagos])

    useEffect(() => {
        setTimeout(() => observacionRef.current?.focus(), 100)
    }, [])

    const [ generateReporte, ] = useReporte({
        callback: (reporte, data, message) => {
            if (data) {
                if (data.tickets) {
                    printTickets(data.tickets)
                }
                else {
                    printTicket(data)
                }
            }
            else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message)
            }
        },
        mode: 'json'
    })

    const emitirCajaTicket = (idMovimientoCaja, individual) => {
        const reporteName = individual ? "CajaTicketIndividual" : "CajaTicket"
        generateReporte(reporteName, { idMovimientoCaja })
    }

    const AplicarCobranza = useCallback((individual = false) => {
        if (isProcessing) return
        setIsProcessing(true)
        setIsLoading(true)

        const paramsUrl = `/movimiento/cobranza/${cajaResumen.caja.id}`
        const dataBody = {
            observacion: formValues.observacion,
            mediosPagos: mediosPagos,
            recibos: recibos.map(x => x.id)
        }

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CAJA,
            paramsUrl,
            dataBody,
            res => res.json().then(({idMovimientoCaja}) => {
                emitirCajaTicket(idMovimientoCaja, individual)
                onConfirm()
                setIsLoading(false)
            }),
            res => {
                onRequestNoSuccess(res)
                setIsProcessing(false)
                setIsLoading(false)
            },
            err => {
                onRequestError(err)
                setIsProcessing(false)
                setIsLoading(false)
            },
        )
    }, [isProcessing, cajaResumen, mediosPagos, recibos, formValues.observacion])

    const onClickConfirm = useCallback((individual = false) => {
        if (Math.abs(formValues.importeCobro - formValues.importePago) >= 0.01) return ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'El monto pagado difiere del monto a cobrar')

        AplicarCobranza(individual)
    }, [formValues.importeCobro, formValues.importePago, AplicarCobranza])

    // Keyboard: Enter para confirmar, Shift+Enter para ticket individual
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && e.shiftKey && recibos.length > 1) {
            e.preventDefault()
            onClickConfirm(true)
        }
        else if (e.key === 'Enter') {
            e.preventDefault()
            onClickConfirm(false)
        }
    }, [onClickConfirm, recibos.length])

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    return (
        <Modal
            onEscape={onCancel}
            size="xl"
            className="cobranzas-medio-pago-modal"
            title="Confirmación"
            body={(
                <div className='row form-basic'>
                    <div className="col-12">
                        <label htmlFor="importeCobro" className="form-label">Monto abonado</label>
                        <InputNumber
                            precision={2}
                            name="importeCobro"
                            placeholder=""
                            className="form-control"
                            value={formValues.importeCobro}
                            disabled={true}
                        />
                    </div>
                    <div className="col-12">
                        <label htmlFor="observacion" className="form-label">Observaciones</label>
                        <input
                            ref={observacionRef}
                            type="text"
                            name="observacion"
                            placeholder=""
                            className="form-control"
                            value={formValues.observacion}
                            onChange={formHandle}
                            maxLength={250}
                        />
                    </div>
                </div>
            )}
            footer={(
                <div className="cobranzas-confirmacion-footer">
                    <button className="btn back-button" onClick={onCancel}>
                        <span>Volver a cobranza</span>
                        <span className="cobranzas-kbd">ESC</span>
                    </button>
                    <div className="cobranzas-confirmacion-footer-actions">
                        {recibos.length > 1 && (
                            <button className="btn action-button" onClick={() => onClickConfirm(true)} disabled={isProcessing}>
                                <span>Imprimir por boleta</span>
                                <span className="cobranzas-kbd">Shift+Enter</span>
                            </button>
                        )}
                        <button className="btn action-button" onClick={() => onClickConfirm(false)} disabled={isProcessing}>
                            <span>Imprimir ticket</span>
                            <span className="cobranzas-kbd">Enter</span>
                        </button>
                    </div>
                </div>
            )}
        />
    )
}

export default CobranzaCajaModal
